'use client';

import * as React from 'react';
import Image from 'next/image';
import { ArrowLeft, ArrowRight, Quote } from 'lucide-react';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { Button } from '@/components/ui/button';

type Voice = {
  id?: number | string;
  name: string;
  role?: string;
  quote: string;
};

const FALLBACK_VOICES: Voice[] = [
  {
    name: 'L&T Infrastructure',
    quote:
      'We certify that Wonder Cement is being used at the prestigious DFCC project. We are fully satisfied with the quality and service performance—a partnership built on reliability.',
  },
  {
    name: 'Wonder Cement Partners',
    role: 'Infrastructure consortium',
    quote:
      'Consistent strength, timely supply, and technical support have made Wonder Cement our preferred partner on multiple national corridor and highway programmes.',
  },
  {
    name: 'Project delivery teams',
    role: 'Pan-India',
    quote:
      'Quality that holds up to rigorous testing and service that keeps sites moving—this is the standard we associate with Wonder Cement on landmark builds.',
  },
];

interface LandmarkVoicesOfTrustProps {
  title?: string;
  items?: Voice[];
  quoteIconUrl?: string | null;
}

export default function LandmarkVoicesOfTrust({
  title = 'Voices of Trust',
  items,
  quoteIconUrl = null,
}: LandmarkVoicesOfTrustProps) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);

  const voices = items && items.length > 0 ? items : FALLBACK_VOICES;

  React.useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  const active = voices[current - 1] ?? voices[0];

  return (
    <section className="bg-[#161618] py-10 text-white sm:py-12 md:py-14">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-center text-3xl font-semibold text-white sm:text-4xl md:text-5xl">
          {title}
        </h2>

        <div className="mt-8 grid grid-cols-1 gap-8 sm:mt-10 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-12">
          <div className="flex flex-col">
            <div>
              <p className="text-xl font-semibold text-white sm:text-2xl">{active.name}</p>
              {active.role ? (
                <p className="mt-1 text-sm text-white/60 sm:text-base">{active.role}</p>
              ) : null}
            </div>

            <div className="mt-5 flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-12 w-12 min-h-[48px] min-w-[48px] rounded-md border border-white/10 bg-[#1d1d20] text-white hover:bg-white/[0.08] hover:text-white"
                onClick={() => api?.scrollPrev()}
                aria-label="Previous testimonial"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="sr-only">Previous testimonial</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-12 w-12 min-h-[48px] min-w-[48px] rounded-md border border-white/10 bg-[#1d1d20] text-white hover:bg-white/[0.08] hover:text-white"
                onClick={() => api?.scrollNext()}
                aria-label="Next testimonial"
              >
                <ArrowRight className="h-5 w-5" />
                <span className="sr-only">Next testimonial</span>
              </Button>
            </div>
          </div>

          <Carousel setApi={setApi} opts={{ align: 'start', loop: true }} className="w-full">
            <CarouselContent className="-ml-0">
              {voices.map((item, index) => (
                <CarouselItem
                  key={`landmark-voice-${item.id ?? index}-${item.name}`}
                  className="pl-0"
                >
                  <article className="max-w-4xl">
                    {quoteIconUrl ? (
                      <Image
                        src={quoteIconUrl}
                        alt=""
                        width={40}
                        height={40}
                        className="h-10 w-10 shrink-0 object-contain"
                        aria-hidden
                      />
                    ) : (
                      <Quote
                        className="h-10 w-10 shrink-0 fill-current text-[#E31E24]"
                        aria-hidden
                      />
                    )}
                    <p className="mt-4 text-xl font-normal !leading-[1.5] text-white sm:text-2xl md:text-[2rem]">
                      {item.quote}
                    </p>
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>
  );
}
